// src/components/PostAd/ReviewAd.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import { auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { usePostAd } from '../../contexts/PostAdContext';
import { submitAdToFirebase } from './SubmitAd';
import { FiMapPin, FiTag, FiCheckCircle, FiInfo } from 'react-icons/fi';
import { CATEGORY_FORM_SPECS } from '../../constants/FormSpecs';
import { getUserProfile, } from '../../services/ProfileService';
import { featureTheAd, } from '../../services/AdManagement';
import '../../pages/PostAd/PostAd.css';


const ReviewAd = () => {
  const context = usePostAd();
  const navigate = useNavigate();
  const [isPublishing, setIsPublishing] = useState(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [profile, setProfile] = useState(null);
  const [wantsFeatured, setWantsFeatured] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const user = auth.currentUser;

  const { adData, previews, selectedCategory, location } = context;

  const handleToggleChange = (e) => {
    if (e.target.checked) {
      setShowConfirm(true); // Open the subtle dialog
    } else {
      setWantsFeatured(false);
    }
  };

  const handleConfirm = () => {
    setWantsFeatured(true);
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    // No need to reset wantsFeatured as it's already false
  };

  const fetchProfile = async () => {
    try {
      const data = await getUserProfile(user.uid);
      setProfile(data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchProfile();
    }
  }, [user]);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const adId = await submitAdToFirebase(context);
      // 2. If user chose to feature and has credits, call the featured function
      if (wantsFeatured && profile?.credits?.featured?.available > 0) {
        await featureTheAd(user.uid, adId);
      }
      navigate(`/success/${adId}`);
    } catch (error) {
      alert("Error publishing ad: " + error.message);
      setIsPublishing(false);
    }
  };

  // Get specs for the current category to display labels correctly
  const specificFields = CATEGORY_FORM_SPECS[selectedCategory] || [];

  // Helper to format the full location string
  const getFullLocation = () => {
    if (!location) return "Location not set";
    const parts = [location.neighbourhood, location.village, location.city, location.state]
      .filter(part => part && part.length > 0);
    // Use Set to remove duplicates (e.g., if neighborhood and village are same)
    return [...new Set(parts)].join(', ');
  };

  return (
    <div className="review-container">
      <div className="review-header-section">
        <h2>Review your listing</h2>
        <p>Check your details one last time before going live.</p>
      </div>

      <div className="review-grid">
        {/* LEFT: Image Gallery/Carousel */}
        <div className="review-visuals">
          <div className="main-image-preview">
            <img src={previews[activePhotoIndex]} alt="Main" />
            <div className="photo-count-badge">
              {activePhotoIndex + 1} / {previews.length}
            </div>
          </div>
          <div className="thumbnail-carousel">
            {previews.map((url, index) => (
              <div
                key={url}
                className={`thumb-item ${activePhotoIndex === index ? 'active' : ''}`}
                onClick={() => setActivePhotoIndex(index)}
              >
                <img src={url} alt="thumb" />
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT: Ad Details Summary */}
        <div className="review-content-card">
          <div className="review-main-info">
            <h1 className="review-title">{adData.title}</h1>
            <h2 className="review-price">₹{Number(adData.price).toLocaleString('en-IN')}</h2>

            <div className="review-meta-item">
              <FiMapPin className="icon" />
              <span>{getFullLocation()}</span>
            </div>
          </div>

          <div className="review-specs-section">
            <h4><FiInfo /> Item Specifications</h4>
            <div className="specs-grid">
              {specificFields.map((field) => {
                const value = adData[field.name];
                if (!value) return null;
                return (
                  <div key={field.name} className="spec-item">
                    <label>{field.label}</label>
                    <p>{value} {field.suffix || ''}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="review-description">
            <h4>Description</h4>
            <p>{adData.description}</p>
          </div>
        </div>
      </div>

      {profile?.credits?.featured?.available > 0 && (

        <div className="featured-opt-in">
          <div className="credit-badge">Free Featured Ads: {profile?.credits?.featured?.available || 0}</div>

          <label className="toggle-container">
            <input
              type="checkbox"
              checked={wantsFeatured} // Controlled component
              onChange={handleToggleChange}
            />
            <span>Feature this ad for 7 days (on top of results)</span>
          </label>
          {/* SUBTLE DIALOG */}
          <AnimatePresence>
            {showConfirm && (
              <div className="modal-overlay">
                <motion.div
                  className="subtle-dialog"
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: 20 }}
                >
                  <div className="dialog-icon">✨</div>
                  <h3>Feature this ad?</h3>
                  <p>This will use <strong>1 credit</strong>. Once made featured, it cannot be undone.</p>

                  <div className="dialog-actions">
                    <button className="btn-cancel" onClick={handleCancel}>Not now</button>
                    <button className="btn-confirm" onClick={handleConfirm}>Yes, Feature it</button>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </div>
      )}

      <div className="review-actions">
        <button
          className="magic-action-btn"
          onClick={handlePublish}
          disabled={isPublishing}
        >
          {isPublishing ? (
            <div className="loader-flex"><div className="spinner-white"></div> Publishing...</div>
          ) : "Confirm & Post Ad Now"}
        </button>

        <button className="reset-btn" onClick={() => context.setStep(3)}>
          Edit Details
        </button>
      </div>
    </div>
  );
};

export default ReviewAd;